import { useQuery } from '@tanstack/react-query';

import { warehouseApi } from 'api/warehouse';

export const ITEM_MASTERS_QUERY_KEY = 'itemMasters';

/**
 * Fetches the item master list used by procurement workflows.
 *
 * Results are cached to avoid refetching a large static-ish dataset.
 *
 * @param {Object} [options]
 * @param {boolean} [options.activeOnly=true] - Limits results to active item masters when true.
 * @param {boolean} [options.enabled=true] - Enables the query when true.
 * @param {boolean} [options.includeRecipeCards=false] - When false (default), omits L4 Recipe Cards.
 * @param {boolean} [options.mmOnly=true] - Limits results to Misfits Market item masters when true.
 * @returns {import('@tanstack/react-query').UseQueryResult<Array<Object>, Error>}
 */
export const useItemMasterQuery = ({
    activeOnly = true,
    enabled = true,
    includeRecipeCards = false,
    mmOnly = true,
} = {}) =>
    useQuery({
        queryKey: [
            ITEM_MASTERS_QUERY_KEY,
            activeOnly ? 'active' : 'all',
            mmOnly ? 'mmOnly' : 'allSources',
            includeRecipeCards ? 'withRecipeCards' : 'noRecipeCards',
        ],
        enabled,
        queryFn: async () => {
            const response = await warehouseApi.getItemMaster(activeOnly, mmOnly, { includeRecipeCards });
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to fetch item masters');
            }

            return Array.isArray(response?.data?.rows) ? response.data.rows : [];
        },
        staleTime: 15 * 60_000,
        gcTime: 30 * 60_000,
    });
